/**
 * Colours the name "MyVaidyam" wherever it is written in the page text the
 * way the logo has it: "My" in the logo's copper, "Vaidyam" in its teal. On
 * a dark background (where the text itself is light) only "My" changes, to
 * a lighter copper, so "Vaidyam" stays readable.
 *
 * This uses the CSS Custom Highlight API: the ranges are painted by CSS
 * (::highlight(mv-…) in index.css) and the DOM is never touched, so React and
 * Google Translate, which both own these text nodes, are unaffected. A
 * browser without the API shows the name as before.
 *
 * Left alone: web addresses and emails (myvaidyam.com, care@myvaidyam.com),
 * the lowercase handle, and controls whose colours flip on hover (buttons,
 * menu items, tabs), where a fixed teal could vanish into a teal hover.
 */
const NAME = /\bMy ?Vaidyam\b/gi;
const SKIP =
  "script,style,textarea,input,select,option,button,label,nav,[role=button],[role=option],[role=menuitem],[role=tab],[contenteditable]";

/** "light" when the text is drawn light (so it sits on a dark ground), "dark" when dark, null when invisible */
function inkOf(el: Element): "light" | "dark" | null {
  const nums = getComputedStyle(el).color.match(/[\d.]+/g)?.map(Number);
  if (!nums || nums.length < 3) return "dark";
  if (nums.length > 3 && nums[3] < 0.2) return null; // transparent text, e.g. a gradient fill
  const [r, g, b] = nums.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? "light" : "dark";
}

export function startBrandColors() {
  if (typeof CSS === "undefined" || !("highlights" in CSS) || typeof Highlight === "undefined") return;
  const my = new Highlight();
  const vaidyam = new Highlight();
  const myOnDark = new Highlight();
  CSS.highlights.set("mv-my", my);
  CSS.highlights.set("mv-vaidyam", vaidyam);
  CSS.highlights.set("mv-my-on-dark", myOnDark);

  const paint = () => {
    my.clear();
    vaidyam.clear();
    myOnDark.clear();
    const walk = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (/vaidyam/i.test(n.nodeValue ?? "") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });
    for (let n = walk.nextNode(); n; n = walk.nextNode()) {
      const el = n.parentElement;
      if (!el || el.closest(SKIP)) continue;
      const text = n.nodeValue ?? "";
      let ink: "light" | "dark" | null | undefined;
      for (const m of text.matchAll(NAME)) {
        const start = m.index ?? 0;
        const end = start + m[0].length;
        if (m[0][0] !== "M") continue; // "myvaidyam": a handle or address
        if (/[@/.]/.test(text[start - 1] ?? "") || /^\.[a-z]/i.test(text.slice(end, end + 2))) continue;
        if (ink === undefined) ink = inkOf(el);
        if (ink === null) break;
        const first = new Range();
        first.setStart(n, start);
        first.setEnd(n, start + 2);
        if (ink === "light") {
          myOnDark.add(first);
          continue;
        }
        const rest = new Range();
        rest.setStart(n, end - 7);
        rest.setEnd(n, end);
        my.add(first);
        vaidyam.add(rest);
      }
    }
  };

  // pages render and change after load (routes, accordions, translation):
  // repaint once the DOM settles, when the browser is idle, so it never holds
  // up the page (a slider turning every few seconds triggers it)
  let pending = false;
  const later = () => {
    if (pending) return;
    pending = true;
    const go = () => { pending = false; paint(); };
    // Safari before 18 has no requestIdleCallback
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(go, { timeout: 1000 });
    else setTimeout(go, 120);
  };
  new MutationObserver(later).observe(document.body, { childList: true, subtree: true, characterData: true });
  later();
}
